<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <div ref="scrollElement" :class="containerStyles" :key="componentKey">
    <div :class="videoContainer" v-if="allowVideo">
      <header2 v-if="title" :text="title" :isUmbraco="isUmbraco" :theme="themeType" />
      <rich-text
        v-if="description"
        :text="description"
        :isUmbraco="isUmbraco"
        :theme="themeType"
        classes="mt-5 lg:mt-6"
      />
      <vimeo-player :videoId="parseInt(videoId)" :portrait="false" :loop="videoLoop" />
    </div>
    <div :class="videoContainer" v-else>
      <div
        class="aspect-video bg-orange flex justify-center items-center text-white text-xxl p-5"
      >
        <p>
          <button @click="showCookieConsentDialog" class="btn btn-primary mt-4">
            {{ setCookie }}
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Window {
  Cookiebot?: {
    renew: () => void;
  };
}
import VimeoPlayer from "@molecules/VimeoPlayer/VimeoPlayer.ce.vue";
import { IVideoProps } from "@/types/components/IVideoComponentProps";
import {
  computed,
  onMounted,
  toRefs,
  ref,
  Ref,
  onUnmounted,
  watch,
  onBeforeMount,
} from "vue";
import { changeTheme, getThemeType, getCookie } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import { getTranslations } from "@/util/api";
import Header2 from "@components/Typography/Header2.ce.vue";
import RichText from "@components/Typography/RichText.ce.vue";

const containerRef: Ref<HTMLDivElement | null> = ref(null);
const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);
const setCookie = ref<string | null>();
const componentKey = ref(0);

const props = withDefaults(defineProps<IVideoProps>(), {
  isUmbraco: false,
  theme: "sand",
});

const { title, description, videoId, theme, videoLoop, isUmbraco, videoSize } = toRefs(
  props
);

const fetchInitialData = async () => {
  [setCookie.value] = await Promise.all([getTranslations("VideoCookie")]);
};

onBeforeMount(fetchInitialData);

const onEnter = () => {
  console.log("title  : ", title && title.value);
  if (theme.value) {
    changeTheme(theme.value);
  }
};
const onExit = () => {};

onMounted(() => {
  updateAllowVideo();
  observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
    threshold: 0.1,
  });
  window.addEventListener("CookiebotOnAccept", onCookieConsentChange);
  window.addEventListener("CookiebotOnDecline", onCookieConsentChange);
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
  window.removeEventListener("CookiebotOnAccept", onCookieConsentChange);
  window.removeEventListener("CookiebotOnDecline", onCookieConsentChange);
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const onCookieConsentChange = () => {
  updateAllowVideo();
  componentKey.value++; // forces the entire section to re-render
};

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} sectionContainer md:min-h-full py-10 lg:py-[60px]`;
});

const videoContainer = computed(() => {
  //Create a switch based on videoSize.value
  switch (videoSize.value) {
    case "lg":
      return `col-start-1 col-span-full md:col-span-12`;
    case "md":
      return `md:col-start-3 md:col-span-10 col-start-2 col-span-12`;
    case "sm":
      return `md:col-start-5 md:col-span-6 col-start-3 col-span-12`;
    default:
      return `md:col-start-2 md:col-span-10 col-start-2 col-span-12`;
  }
});

const missingCookieLabel = computed(async () => {
  let label = await getTranslations("VideoCookie");
  return label;
});

const allowVideo = ref<boolean | null>(null);

const updateAllowVideo = () => {
  const cookieString = getCookie("CookieConsent");
  if (!cookieString) {
    allowVideo.value = null;
    return;
  }
  const marketingMatch = cookieString.match(/marketing:\s*(true|false)/);
  if (marketingMatch) {
    allowVideo.value = JSON.parse(marketingMatch[1]);
  } else {
    console.error("Marketing property not found in cookie string");
    allowVideo.value = null;
  }
};

// const allowVideo = computed(() => {
//   const cookieString = getCookie("CookieConsent");
//   // COOKIE STRING FOR LOCAL TESTING
//   //const cookieString = `{stamp:'JiFAD24X96gRckejqUkd9Etot7hUEJ4nGYuJYimcXtovtxwhziwrog==',necessary:true,preferences:true,statistics:true,marketing:true,method:'explicit',ver:2,utc:1712579039412,region:'dk'}`;
//   if (!cookieString) return null;
//   const marketingMatch = cookieString.match(/marketing:\s*(true|false)/);

//   if (marketingMatch) {
//     // Extract the matched value and parse it to a boolean
//     const marketingValue = JSON.parse(marketingMatch[1]);
//     console.log("marketingValue: ", marketingValue);

//     return marketingValue;
//   } else {
//     console.error("Marketing property not found in cookie string");
//     return null;
//   }
// });
const showCookieConsentDialog = () => {
  if (window.Cookiebot) {
    window.Cookiebot.renew();
  } else {
    console.error("Cookiebot is not available");
  }
};
</script>
